/**
 * P8 aus `docs/LOOP-ANFRAGEN-WER-IST-DRAN.md` — der Drawer **„Angebots-Verlauf"** (Entwurf 27c,
 * `docs/KONZEPT-ANFRAGEN-WER-IST-DRAN.md` § 9), die Vorgangsansicht.
 *
 * Diese Datei **formuliert nichts und rechnet nichts**. Jeder Satz, jede Beschriftung, jeder
 * Betrag und jede Zeitangabe kommt fertig aus `GET /api/events/:id/angebots-verlauf` — dort aus
 * `leiteAngebotsVerlaufAb` (`@maestro/angebots-verlauf`). Genau so ist es bei der Anfragenliste
 * (P3) und der Auswertung (P7): zweimal formuliert hieße zweimal anders formuliert, und dann
 * stünde in der Liste etwas anderes als im Drawer daneben.
 *
 * ── Was hier bewusst NICHT passiert ─────────────────────────────────────────────────────────
 * Kein Betrag wird geteilt, gerundet oder umgerechnet (€/Person kommt fertig vom Server, gerechnet
 * aus `offer_options.amount_total_cents` und `guest_count`). Keine Ampel, kein Score, keine
 * Empfehlung. Und **nichts wird gesendet**: „✨ Nachfass-Mail entwerfen" FÜHRT zur
 * Veranstaltungsseite, an der das Nachfassen schon wohnt (`POST /api/events/:id/offers/resend`).
 *
 * ── Fehlendes wird gezeigt, nicht versteckt ─────────────────────────────────────────────────
 * Wo der Server einen `…Fehlt`-Grund liefert, steht er da: „Kein Preis je Person — die Gästezahl
 * der Variante fehlt", „Wer geöffnet hat, wird bewusst nicht erfasst". Eine Zeile ohne Zeitstempel
 * gibt es nicht; dass es sie gab, sagt `verlauf.ausgelassen`.
 *
 * ── Mobil ohne zweiten Datenweg ─────────────────────────────────────────────────────────────
 * Anders als die Anfragenliste (P3.4, Tabelle ⇄ Karten) ist hier jede Einheit schon eine Zeile aus
 * wenigen Werten: dieselbe DOM fließt über Tailwind-Breakpoints um. Ein zweiter Zweig wäre eine
 * zweite Wahrheit ohne Gewinn. Alle Übergänge laufen über `motion-safe:` —
 * `prefers-reduced-motion` wird respektiert.
 */
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import type { AngebotsVerlauf, Ereigniszeile, OptionZeile } from "@maestro/angebots-verlauf";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Drawer, ErrorNote, Icon, LoadingRows, ButtonRouterLink } from "./components";
import { AngebotsEinblicke } from "./AngebotsEinblicke";
import type { Einblicke } from "../lib/einblicke";
import { A_LA_CARTE_VOR_ORT, NACH_KARTE } from "../lib/a-la-carte";

const LABEL = "font-label-md text-[11px] uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim";
const TEXT = "font-body-md text-body-md leading-relaxed text-on-background dark:text-inverse-on-surface";
const KLEIN = "font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim";

/** Eine Variante: Bezeichnung links, €/Person und Gesamt rechts — mobil untereinander. */
function OptionsZeile({ zeile, aLaCarte = false }: { zeile: OptionZeile; aLaCarte?: boolean }) {
  return (
    <li className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[1fr_auto_auto] sm:items-baseline sm:gap-6">
      <span className={TEXT}>
        {zeile.bezeichnung}
        {zeile.gewaehlt && zeile.gewaehltText && (
          <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 font-label-md text-[11px] text-primary">
            {zeile.gewaehltText}
          </span>
        )}
        {!zeile.gesendet && <span className={`ml-2 ${KLEIN}`}>(nicht gesendet)</span>}
      </span>
      {/* Migration 731: „À la carte“ hat keinen Betrag - die Zeile sagt es, statt „0,00 €“ oder „Kein Preis“ zu zeigen. */}
      <span className={`tabular-nums sm:w-40 sm:text-right ${aLaCarte || zeile.proPerson ? TEXT : KLEIN}`}>
        {aLaCarte ? A_LA_CARTE_VOR_ORT : (zeile.proPerson ?? zeile.proPersonFehlt)}
      </span>
      <span className={`tabular-nums sm:w-40 sm:text-right ${aLaCarte || zeile.gesamt ? TEXT : KLEIN}`} data-testid={aLaCarte ? "verlauf-nach-karte" : undefined}>
        {aLaCarte ? NACH_KARTE : (zeile.gesamt ?? zeile.gesamtFehlt)}
      </span>
    </li>
  );
}

/** Eine Ereigniszeile: Zeit · Was · Person · Beleg. Ohne Zeit gäbe es sie nicht (P8.3). */
function VerlaufsZeile({ zeile }: { zeile: Ereigniszeile }) {
  return (
    <li className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[10.5rem_1fr] sm:gap-4">
      <span className={`tabular-nums ${KLEIN}`}>{zeile.zeit}</span>
      <span>
        <span className={`block ${TEXT}`}>{zeile.text}</span>
        <span className={`mt-0.5 block ${KLEIN}`}>
          {zeile.person ?? zeile.personFehlt}
          {" · "}
          <span className="opacity-70">{zeile.beleg}</span>
        </span>
      </span>
    </li>
  );
}

export function AngebotsVerlaufDrawer({
  eventId,
  open,
  onClose,
  onGeaendert,
}: {
  eventId: string;
  open: boolean;
  onClose: () => void;
  /** Damit die Seite darunter nachlädt, wenn hier etwas notiert wurde. */
  onGeaendert?: () => void;
}) {
  // Erst laden, wenn der Drawer offen ist — ein geschlossener Drawer braucht keine Abfrage.
  const { data, loading, error, reload } = useApi<AngebotsVerlauf & { einblicke?: Einblicke }>(
    open && eventId ? `/api/events/${eventId}/angebots-verlauf` : null,
  );
  const [formularOffen, setFormularOffen] = useState(false);
  const [notiz, setNotiz] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function telefonischNotieren(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/telefonisch-erreicht`, "POST", { notiz });
      setFormularOffen(false);
      setNotiz("");
      reload();
      onGeaendert?.();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Angebots-Verlauf">
      {loading && <LoadingRows rows={4} />}
      {error && <ErrorNote message={error} onRetry={reload} />}
      {data && (
        <div className="space-y-7">
          {/* ── P8.1: der Kopf-Satz. Version, Liegezeit, Öffnungen, Werktage — fertig vom Server. */}
          <section>
            <p className={`font-body-lg text-body-lg leading-relaxed ${TEXT}`}>{data.kopf.satz}</p>

            {data.aktionen.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {data.aktionen.map((a) =>
                  a.art === "navigation" && a.ziel ? (
                    <ButtonRouterLink key={a.schluessel} to={a.ziel} variant="secondary">
                      {a.text}
                    </ButtonRouterLink>
                  ) : (
                    <Button
                      key={a.schluessel}
                      variant="secondary"
                      onClick={() => setFormularOffen(true)}
                    >
                      {a.text}
                    </Button>
                  ),
                )}
              </div>
            )}
            {data.zusicherung && <p className={`mt-2 ${KLEIN}`}>{data.zusicherung}</p>}

            {/* Die einzige Aktion, die AUFNIMMT statt hinzuführen — wie „Grund notieren" (P4.2). */}
            {formularOffen && (
              <form onSubmit={telefonischNotieren} className="mt-4 rounded-xl border border-[#F0E7D6] p-4 dark:border-white/[0.06]">
                <label className={`block ${LABEL}`} htmlFor="telefon-notiz">
                  Was wurde besprochen? (optional)
                </label>
                <textarea
                  id="telefon-notiz"
                  className="mt-2 w-full rounded-lg border border-outline-variant bg-transparent p-2 font-body-sm text-body-sm"
                  rows={3}
                  value={notiz}
                  onChange={(ev) => setNotiz(ev.target.value)}
                />
                <p className={`mt-1 ${KLEIN}`}>
                  Ohne Notiz steht im Verlauf nur, dass telefonisch erreicht wurde — es wird nichts
                  ergänzt.
                </p>
                <div className="mt-3 flex gap-2">
                  <Button type="submit" disabled={busy}>
                    {busy ? "…" : "Telefonat notieren"}
                  </Button>
                  <Button variant="ghost" type="button" onClick={() => setFormularOffen(false)}>
                    Abbrechen
                  </Button>
                </div>
                {fehler && <p className="mt-2 font-body-sm text-body-sm text-error">{fehler}</p>}
              </form>
            )}
          </section>

          {/* ── P8.2: die gesendeten Optionen. */}
          <section>
            <h3 className={LABEL}>{data.optionen.ueberschrift}</h3>
            {data.optionen.leer ? (
              <p className={`mt-2 ${KLEIN}`}>{data.optionen.leer}</p>
            ) : (
              <ul className="mt-1 divide-y divide-[#F6F0E4] dark:divide-white/[0.05]">
                {data.optionen.zeilen.map((z) => (
                  <OptionsZeile key={z.id} zeile={z} aLaCarte={z.aLaCarte} />
                ))}
              </ul>
            )}
            {data.optionen.ohneWahl && (
              <p className={`mt-3 ${KLEIN}`}>{data.optionen.ohneWahl.satz}</p>
            )}
          </section>

          {/* ── Einblicke (Migration 730): Aufrufe, Geräteklasse, Dauer, Lesefortschritt, Stationen. */}
          <AngebotsEinblicke einblicke={data.einblicke} />

          {/* ── P8.3: der Verlauf. */}
          <section>
            <h3 className={LABEL}>Verlauf</h3>
            {data.verlauf.leer ? (
              <p className={`mt-2 ${KLEIN}`}>{data.verlauf.leer}</p>
            ) : (
              <ul className="mt-1 divide-y divide-[#F6F0E4] dark:divide-white/[0.05]">
                {data.verlauf.zeilen.map((z) => (
                  <VerlaufsZeile key={`${z.art}-${z.zeitpunkt}-${z.text}`} zeile={z} />
                ))}
              </ul>
            )}
            {data.verlauf.ausgelassen && (
              <p className={`mt-3 ${KLEIN}`}>{data.verlauf.ausgelassen}</p>
            )}
            <p className={`mt-3 ${KLEIN}`}>
              Hier steht der Angebotsweg. Rechnungen, Zahlungen und alles Übrige stehen unverändert
              im vollständigen Verlauf des Vorgangs.
            </p>
          </section>
        </div>
      )}
    </Drawer>
  );
}

/** Der Knopf, der den Drawer öffnet — samt eigenem Zustand, damit die Seite nichts davon führt. */
export function AngebotsVerlaufKarte({ eventId, onGeaendert }: { eventId: string; onGeaendert?: () => void }) {
  const [offen, setOffen] = useState(false);
  return (
    <>
      <Button
        variant="secondary"
        className="w-full motion-safe:transition-colors"
        onClick={() => setOffen(true)}
      >
        <Icon name="history" className="text-[18px]" />
        Angebots-Verlauf
      </Button>
      <AngebotsVerlaufDrawer
        eventId={eventId}
        open={offen}
        onClose={() => setOffen(false)}
        onGeaendert={onGeaendert}
      />
    </>
  );
}
